/**
 * ProfileNudgeCard — the "get matched" card pinned at the top of the find pane
 * (PPL-486).
 *
 * Matching runs on the signed-in user's own profile, so a thin profile makes
 * the whole meet-new-ppl path quietly useless: the interests tab comes back
 * empty and the empty state says only "no users matching your interests yet",
 * which reads as "nobody is out there" rather than "we know nothing about you".
 * This card is the missing "why".
 *
 * It names ONE thing at a time — the first gap in the order interests → photo
 * → bio — because interests are the only one matching actually reads, and a
 * three-item checklist at the top of a discovery page reads as a wall. Every
 * string is static: the card says nothing about the user or about who it would
 * match them with, so there is nothing here that belongs on a backend.
 *
 * Not dismissable by design — it disappears the moment the gap it names is
 * filled, and the host re-checks on focus so that happens on the way back from
 * the editor.
 */

import React from 'react';
import { Camera, FileText, Tag } from 'react-feather';
import { Link } from 'react-router-dom';
import GlassPanel from './GlassPanel';
import '../styles/modern.css';

export type ProfileNudgeTarget = 'interests' | 'photo' | 'bio';

/** The shape this card reads off users/{uid}. Everything is optional. */
export interface ProfileNudgeProfile {
  /** Ships as a CSV string on some writers and an array on others. */
  interests?: string | string[] | null;
  profileImageUrl?: string | null;
  aboutMe?: string | null;
}

function hasInterests(value: ProfileNudgeProfile['interests']): boolean {
  if (Array.isArray(value)) {
    return value.some((item) => typeof item === 'string' && item.trim().length > 0);
  }
  if (typeof value === 'string') {
    return value.split(',').some((item) => item.trim().length > 0);
  }
  return false;
}

const hasText = (value: string | null | undefined): boolean =>
  typeof value === 'string' && value.trim().length > 0;

/**
 * The first gap in the profile, or null when nothing is missing. Order matters:
 * interests first, because that is the one matching reads.
 */
export function firstProfileGap(
  profile: ProfileNudgeProfile | null | undefined,
): ProfileNudgeTarget | null {
  if (!profile) return null;
  if (!hasInterests(profile.interests)) return 'interests';
  if (!hasText(profile.profileImageUrl)) return 'photo';
  if (!hasText(profile.aboutMe)) return 'bio';
  return null;
}

const COPY: Record<ProfileNudgeTarget, { body: string; cta: string; to: string }> = {
  interests: {
    body: 'add your interests so f8 can find your ppl',
    cta: 'add interests',
    to: '/profile/edit#interests',
  },
  photo: {
    body: 'add a photo so your ppl recognise you',
    cta: 'add a photo',
    to: '/profile/edit',
  },
  bio: {
    body: 'say a line about yourself so f8 can match you',
    cta: 'add a bio',
    to: '/profile/edit',
  },
};

function glyph(target: ProfileNudgeTarget) {
  switch (target) {
    case 'photo':
      return <Camera size={14} />;
    case 'bio':
      return <FileText size={14} />;
    case 'interests':
    default:
      return <Tag size={14} />;
  }
}

export interface ProfileNudgeCardProps {
  profile: ProfileNudgeProfile | null | undefined;
  style?: React.CSSProperties;
}

export default function ProfileNudgeCard({ profile, style }: ProfileNudgeCardProps) {
  const target = firstProfileGap(profile);
  if (!target) return null;

  const { body, cta, to } = COPY[target];

  return (
    <Link to={to} style={{ textDecoration: 'none', display: 'block', ...style }}>
      <GlassPanel padding={14}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
          <span
            aria-hidden="true"
            style={{
              width: 32,
              height: 32,
              borderRadius: 11,
              flexShrink: 0,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--accent-purple-dim)',
              color: 'var(--accent-purple-muted)',
            }}
          >
            {glyph(target)}
          </span>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>
              get matched
            </div>
            <div style={{ marginTop: 1, fontSize: 11.5, color: 'var(--text-dim)' }}>{body}</div>
          </div>

          <span className="tag-pill tag-pill--sm tag-pill--violet" style={{ flexShrink: 0 }}>
            {cta}
          </span>
        </div>
      </GlassPanel>
    </Link>
  );
}
